import React, { ChangeEvent, ReactNode } from 'react';
import { Field, Form } from 'react-final-form';
import { observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigate } from 'react-router-dom';
import { TafButton, TafNotify } from '@taf-ui/react';
import { Option } from '@taf-ui/core';

import ToggleButtons from '../FormInputs/ToggleButtons/ToggleButtons';
import Labels from '../enums/Labels';
import Steps from '../enums/Steps';
import RouteParams from '../interfaces/RouteParams';
import AppStorage from '../Storage/Storage';
import DraftService from '../Requests/DraftService/DraftService';
import FileInput from '../FileInput/FileInput';
import {
  formatBytes,
  isBrand,
  randomIntFromInterval,
  translate,
  validateHealthDocumentFile,
} from '../utils/utils';
import HealthDocFile from '../interfaces/HealthDocFile';
import DocumentsService from '../Requests/DocumentsService';
import Language from '../enums/Language';
import Brand from '../enums/Brand';

import './HealthDeclarationDocuments.css';
import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';
import ReadSpeakerReader from 'ReadSpeakerReader';

const toggleOptions: Array<Option> = [
  {
    title: Labels.NO,
    value: 'false',
  },
  {
    title: Labels.YES,
    value: 'true',
  },
];

interface ComponentState {
  isUploading: boolean;
  navigateComponent: ReactNode;
  isTotalFilesSizeErrorShown: boolean;
}

class HealthDeclarationDocuments extends React.PureComponent<
  RouteParams,
  ComponentState
> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly documentsService: DocumentsService;

  private readonly locationSearch: string | undefined;

  constructor(props: RouteParams) {
    super(props);
    this.locationSearch = props.locationSearch as string;
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.documentsService = new DocumentsService();
    this.state = {
      isUploading: false,
      navigateComponent: null,
      isTotalFilesSizeErrorShown: false,
    };
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.Documents, 'active', true);
    this.draftService.syncLater();

    if (isBrand(Brand.DHMR)) {
      this.showFileUpload();
    }
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.Documents, 'done', true);
    this.storage.modifyProgressBar(Steps.Documents, 'active', false);
    this.validatePage();
  }

  validatePage = () => {
    const isDHMR = isBrand(Brand.DHMR);
    const isDocuments = this.storage.getIsHealthDeclarationDocuments;

    if (isDHMR) {
      /* For DHMR always perform check by files */
      this.validatePageDependsOnFilesStatus();
    } else if (!isDocuments) {
      /* For rest set valid if no files. If we have files, validation would be done earlier */
      this.storage.modifyProgressBar(Steps.Documents, 'valid', true);
    }
  };

  showFileUpload = () => {
    this.storage.setIsHealthDeclarationDocuments(true);
  };

  toggle = (isHealthDeclarationDocuments: boolean): void => {
    this.storage.setIsHealthDeclarationDocuments(isHealthDeclarationDocuments);

    this.validatePageDependsOnFilesStatus();
  };

  /*
   * Set validity based on current documents and toggle form value.
   *
   * isHealthDeclarationDocuments - value from form for TAF and wefox
   * isHealthDeclarationDocuments - is always true be default for DHMR
   * */
  validatePageDependsOnFilesStatus = () => {
    const documents = this.storage.getHealthDeclarationFileList;
    const isHealthDeclarationDocuments =
      this.storage.getIsHealthDeclarationDocuments;
    if (!isHealthDeclarationDocuments) {
      this.storage.modifyProgressBar(Steps.Documents, 'valid', true);
    } else if (documents.length) {
      this.setFilesValidity();
    } else {
      this.storage.modifyProgressBar(Steps.Documents, 'valid', false);
    }
  };

  goToNextStep = (): void => {
    const { healthDeclarationId } = this.props;

    this.draftService.saveDraft();
    this.setState({
      navigateComponent: (
        <Navigate
          to={`/${healthDeclarationId}/conclusion${this.locationSearch || ''}`}
        />
      ),
    });
  };

  private onSubmit = (): void => {};

  formatFiles = (healthDeclarationFileList: FileList): Array<HealthDocFile> => {
    const list: Array<HealthDocFile> = [];
    const existingDocuments = this.storage.getHealthDeclarationFileList;
    for (let i = 0; i < healthDeclarationFileList.length; i += 1) {
      const { isValid, error } = validateHealthDocumentFile(
        healthDeclarationFileList[i],
        existingDocuments,
      );

      const file: HealthDocFile = {
        id: randomIntFromInterval(1, 1000000),
        description: error,
        isValid,
        file: healthDeclarationFileList[i],
        isLoaded: false,
      };
      list.push(file);
    }
    return list;
  };

  fileUploadHandler = (fileList: FileList): void => {
    const healthDocFiles = this.formatFiles(fileList);
    const documents = this.storage.getHealthDeclarationFileList;
    this.storage.setHealthDeclarationFileList([
      ...documents,
      ...healthDocFiles,
    ]);
    this.setFilesValidity();
  };

  setFilesValidity = (): void => {
    const isAllDocsValid = this.checkIfAllDocumentsValid();
    this.storage.setIsAllDocumentsValid(isAllDocsValid);
    this.storage.modifyProgressBar(
      Steps.Documents,
      'valid',
      this.storage.getAtLeastOneUploaded,
    );
  };

  changeDescription = (
    { target: { value } }: ChangeEvent<HTMLInputElement>,
    id: number,
  ): void => {
    const documents = this.storage.getHealthDeclarationFileList;

    const newDocuments = documents.map((doc) => {
      if (doc.id === id) {
        doc.description = value;
      }
      return doc;
    });

    this.storage.setHealthDeclarationFileList(newDocuments);
  };

  checkIfAllDocumentsValid = (): boolean =>
    !this.storage.getHealthDeclarationFileList.some((file) => !file.isValid);

  removeHealthDeclarationDocument = (id: number): void => {
    const documents = this.storage.getHealthDeclarationFileList;
    const newDocuments = documents.filter((doc) => doc.id !== id);
    this.storage.setHealthDeclarationFileList(newDocuments);
    this.setFilesValidity();
    this.showTotalFileSizeError(false);
  };

  checkTotalSize = (): boolean => {
    const totalFilesSizeToBeUploaded =
      this.storage.getHealthDeclarationFileList.reduce((total, document) => {
        return total + document.file.size;
      }, 0);

    const THIRTY_MB = 31457280;
    return THIRTY_MB > totalFilesSizeToBeUploaded;
  };

  showTotalFileSizeError = (isTotalFilesSizeErrorShown: boolean) => {
    this.setState({
      isTotalFilesSizeErrorShown,
    });
  };

  uploadDocuments = (): void => {
    this.setIsUploading(true);
    this.filterOnlySupportedValidDocuments();

    const isAppropriateTotalSize = this.checkTotalSize();

    if (isAppropriateTotalSize) {
      this.showTotalFileSizeError(false);
      void this.documentsService.uploadDocuments().then(() => {
        this.storage.setCurrentDocumentsAsLoaded();
        this.storage.modifyProgressBar(Steps.Documents, 'valid', true);
        this.setIsUploading(false);
      });
    } else {
      this.showTotalFileSizeError(true);
      this.setIsUploading(false);
    }
  };

  setIsUploading = (isUploading: boolean): void => {
    this.setState({
      isUploading,
    });
  };

  filterOnlySupportedValidDocuments = (): void => {
    const allFiles = this.storage.getHealthDeclarationFileList;
    const onlyValidFiles = allFiles.filter((file) => file.isValid);
    this.storage.setHealthDeclarationFileList(onlyValidFiles);
  };

  translateButtonOptions = (
    options: Array<Option>,
    lang: Language,
  ): Array<Option> =>
    options.map((option) => ({
      ...option,
      title: translate(option.title as Labels, lang),
    }));

  getDescriptionBlock = () => {
    const isDHMR = isBrand(Brand.DHMR);
    const lang = this.storage.getLocale;
    const productSpecificDocumentsInstructions =
      this.storage.getProductSpecificDocumentsInstructions();

    if (isDHMR && productSpecificDocumentsInstructions) {
      const isErrorShown =
        this.storage.getActiveStep?.done && !this.storage.getAtLeastOneUploaded;

      return (
        <>
          {isErrorShown && (
            <TafNotify hideCloseButton={true} state="error">
              {translate(Labels.DOCUMENTS_UPLOAD_AT_LEAST_ONE_ERROR_DHMR, lang)}
            </TafNotify>
          )}
          <section
            className="declaration-documents-label"
            dangerouslySetInnerHTML={{
              __html: productSpecificDocumentsInstructions,
            }}
          ></section>
        </>
      );
    }

    return (
      <section className="declaration-documents-label">
        <p>{translate(Labels.DOCUMENTS_UPLOAD_QUESTION_LABEL, lang)}</p>
        <ul>
          <li>{translate(Labels.DOCUMENTS_UPLOAD_LIST_ITEM_1, lang)}</li>
          <li>{translate(Labels.DOCUMENTS_UPLOAD_LIST_ITEM_2, lang)}</li>
        </ul>
      </section>
    );
  };

  getToggleFormToShowFileLoader = () => {
    const isDHMR = isBrand(Brand.DHMR);
    const lang = this.storage.getLocale;

    if (isDHMR) return null;

    return (
      <Form
        onSubmit={this.onSubmit}
        initialValues={{
          isDocuments: this.storage.getIsHealthDeclarationDocuments,
        }}
        render={(props) => (
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          <form onSubmit={props.handleSubmit}>
            <div
              className="toggle-wrapper"
              aria-label={this.translateButtonOptions(toggleOptions, lang) //Todo: After migration to TAF UI, remove this and use aria-label from ToggleButtons
                .map((option) => option.title)
                .join(', ')}
            >
              <Field
                name="isDocuments"
                render={({ input, meta }) => (
                  <ToggleButtons
                    tafToggleId="documents"
                    options={this.translateButtonOptions(toggleOptions, lang)}
                    input={input}
                    meta={meta}
                    onChangeCallback={this.toggle}
                  />
                )}
              />
            </div>
          </form>
        )}
      />
    );
  };

  render(): React.ReactElement {
    const lang = this.storage.getLocale;
    const hasValidDocumentsToBeUploaded =
      this.storage.getHasValidDocumentsToBeUploaded;
    const fileSize = isBrand(Brand.DHMR) ? '20' : '2';

    const { isUploading, isTotalFilesSizeErrorShown } = this.state;

    if (this.state.navigateComponent) {
      return <>{this.state.navigateComponent}</>;
    }

    return (
      <div id="main-content" lang={languageToReadSpeakerLang(lang)}>
        <ReadSpeakerReader />
        {this.getDescriptionBlock()}

        {this.getToggleFormToShowFileLoader()}

        {this.storage.getIsHealthDeclarationDocuments && (
          <>
            <FileInput fileUploadHandler={this.fileUploadHandler} />
          </>
        )}

        {isTotalFilesSizeErrorShown ? (
          <div className="filesTotalSizeWarning">
            <TafNotify hideCloseButton={true} state="warning">
              {translate(Labels.DOCUMENTS_UPLOAD_FILES_TOTAL_SIZE, lang)}
            </TafNotify>
          </div>
        ) : null}

        {this.storage.getHealthDeclarationFileList.length > 0 && (
          <div className="documents-table-wrapper">
            <table className="documents-table">
              <thead className="documents-table-header">
                <tr>
                  <th>{translate(Labels.HEALTH_DOC_TH1, lang)}</th>
                  <th>{translate(Labels.HEALTH_DOC_TH2, lang)}</th>
                  <th>{translate(Labels.HEALTH_DOC_TH3, lang)}</th>
                  <th>{translate(Labels.HEALTH_DOC_TH4, lang)}</th>
                  <th>{translate(Labels.HEALTH_DOC_TH5, lang)}</th>
                </tr>
              </thead>
              <tbody>
                {this.storage.getHealthDeclarationFileList.map((document) => (
                  <tr key={document.id}>
                    <td className="document-name">{document.file.name}</td>
                    <td className="centered">
                      {formatBytes(document.file.size)}
                    </td>
                    <td className="centered">
                      {document.isValid ? (
                        <span className="upload-succeeded">
                          <FontAwesomeIcon size="lg" icon="check-circle" />
                        </span>
                      ) : (
                        <span className="upload-failed">
                          <FontAwesomeIcon size="lg" icon="times-circle" />
                        </span>
                      )}
                    </td>
                    <td className="description">
                      {document.isValid ? (
                        <input
                          className="description-input"
                          disabled={document.isLoaded}
                          type="text"
                          placeholder={translate(
                            Labels.HEALTH_DOC_DESCRIPTION_PLACEHOLDER,
                            lang,
                          )}
                          value={document.description}
                          onChange={(e): void => {
                            this.changeDescription(e, document.id);
                          }}
                        />
                      ) : (
                        <span>
                          {translate(Labels.HEALTH_DOC_ERROR, lang)}
                          &nbsp;
                          {translate(document.description as Labels, lang, [
                            fileSize,
                          ])}
                        </span>
                      )}
                    </td>
                    <td>
                      {document.isLoaded ? (
                        <span role="presentation">
                          {translate(Labels.HEALTH_DOC_UPLOADED, lang)}
                        </span>
                      ) : (
                        <span
                          role="presentation"
                          className="remove-link"
                          onClick={(): void => {
                            this.removeHealthDeclarationDocument(document.id);
                          }}
                        >
                          {translate(Labels.HEALTH_DOC_REMOVE, lang)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasValidDocumentsToBeUploaded ? (
          <TafButton
            id="uploadDocuments"
            disabled={isUploading}
            onClick={this.uploadDocuments}
            type="button"
            primary={true}
            title={translate(Labels.HEALTH_DOC_CONFIRM_UPLOAD, lang)}
            className="proceed"
          >
            {translate(Labels.HEALTH_DOC_CONFIRM_UPLOAD, lang)}
            &nbsp;
            {isUploading ? (
              <FontAwesomeIcon icon="circle-notch" spin={true} />
            ) : null}
          </TafButton>
        ) : (
          <>
            <TafButton
              id="documentsPageNextBtn"
              onClick={this.goToNextStep}
              type="button"
              primary={true}
              className="proceed"
              title={translate(Labels.NEXT_STEP, lang)}
            >
              {translate(Labels.NEXT_STEP, lang)}
            </TafButton>
          </>
        )}
      </div>
    );
  }
}

export default observer(HealthDeclarationDocuments);
